/**
 * Authentication Context
 *
 * Manages auth state and methods. Uses Supabase Auth (JWT, session persistence)
 * and the profiles table for role, approval, and profile data.
 *
 * Security:
 * - Login enforces is_approved and is_active; unapproved users are signed out.
 * - onAuthStateChange also enforces approval (e.g. after invite acceptance).
 * - Tokens stored in localStorage (Supabase default); auto-refresh before expiry.
 */

import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// Create context
const AuthContext = createContext();

/**
 * AuthProvider Component
 * Manages authentication state and provides auth methods
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  /**
   * Fetch profile data from the profiles table (only needed columns for faster load)
   * Retries once on AbortError (common with Strict Mode / rapid auth state changes)
   */
  const fetchProfile = useCallback(async (userId, retries = 1) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id,name,email,phone,role,avatar,status,created_at,last_login')
      .eq('id', userId)
      .single();

    if (error) {
      const isAbort = error?.message?.includes('AbortError') || error?.code === '';
      if (isAbort && retries > 0) {
        await new Promise((r) => setTimeout(r, 200));
        return fetchProfile(userId, retries - 1);
      }
      if (!isAbort) console.error('Error fetching profile:', error);
      return null;
    }
    return data;
  }, []);

  /** Fire-and-forget last_login update - does not block UI */
  const updateLastLogin = useCallback((userId) => {
    supabase
      .from('profiles')
      .update({ last_login: new Date().toISOString() })
      .eq('id', userId)
      .then(() => {})
      .catch(() => {});
  }, []);

  /**
   * Apply profile and session state
   */
  const applyProfileToState = useCallback((profile, session) => {
    if (!profile) return false;
    if (profile.role !== 'super_admin' && profile.status !== 'active') return false;
    setUser({
      id: profile.id,
      name: profile.name,
      email: profile.email,
      phone: profile.phone,
      role: profile.role,
      avatar: profile.avatar,
      status: profile.status,
      createdAt: profile.created_at,
      lastLogin: profile.last_login,
    });
    setIsAuthenticated(true);
    return true;
  }, []);

  /**
   * Set up auth state on mount and listen for changes
   *
   * IMPORTANT: Supabase has a known deadlock - async calls inside onAuthStateChange
   * cause getSession/subsequent calls to hang. Workaround: defer DB calls with setTimeout.
   * Also: timeout init so we never hang indefinitely (e.g. paused Supabase project).
   */
  useEffect(() => {
    const AUTH_TIMEOUT_MS = 10000;

    const initAuth = async () => {
      try {
        const sessionPromise = supabase.auth.getSession();
        sessionPromise.catch(() => {}); // Prevent unhandled AbortError when timeout wins
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Auth timeout')), AUTH_TIMEOUT_MS)
        );
        const { data: { session: currentSession } } = await Promise.race([
          sessionPromise,
          timeoutPromise,
        ]);

        if (currentSession) {
          setSession(currentSession);
          const profile = await fetchProfile(currentSession.user.id);
          if (profile) {
            if (profile.role !== 'super_admin' && profile.status !== 'active') {
              await supabase.auth.signOut();
            } else {
              applyProfileToState(profile, currentSession);
              updateLastLogin(profile.id);
            }
          }
        }
      } catch (error) {
        if (error?.message === 'Auth timeout' || error?.name === 'AbortError') {
          console.warn('Auth initialization timed out. Redirecting to login.');
        } else {
          console.error('Auth initialization error:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Defer Supabase DB calls to avoid deadlock (known supabase-js bug)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (event === 'SIGNED_IN' && newSession) {
        setSession(newSession);
        setTimeout(async () => {
          const profile = await fetchProfile(newSession.user.id);
          if (profile) {
            if (profile.role !== 'super_admin' && profile.status !== 'active') {
              await supabase.auth.signOut();
            } else {
              applyProfileToState(profile, newSession);
              updateLastLogin(profile.id);
            }
          }
        }, 0);
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setIsAuthenticated(false);
      } else if (event === 'TOKEN_REFRESHED' && newSession) {
        setSession(newSession);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [fetchProfile, updateLastLogin, applyProfileToState]);

  /**
   * Login user
   * @param {Object} credentials - { email, password }
   */
  const login = async (credentials) => {
    const LOGIN_TIMEOUT_MS = 15000;

    const loginTask = async () => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      const profile = await fetchProfile(data.user.id);

      if (!profile) {
        await supabase.auth.signOut();
        return { success: false, error: 'User profile not found. Contact administrator.' };
      }

      if (profile.status === 'suspended') {
        await supabase.auth.signOut();
        return { success: false, error: 'Account is suspended. Please contact administrator.' };
      }

      if (profile.status === 'pending' || profile.status === 'rejected') {
        await supabase.auth.signOut();
        return {
          success: false,
          error: 'Your account is pending approval. Please wait for administrator approval.',
        };
      }

      updateLastLogin(data.user.id);
      setSession(data.session);
      applyProfileToState(profile, data.session);
      return { success: true, user: profile };
    };

    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(
        () => reject(new Error('Connection timed out. Your Supabase project may be paused. Restore it at supabase.com/dashboard')),
        LOGIN_TIMEOUT_MS
      )
    );

    const loginPromise = loginTask();
    loginPromise.catch(() => {}); // Prevent unhandled AbortError when timeout wins

    try {
      return await Promise.race([loginPromise, timeoutPromise]);
    } catch (error) {
      const message = error?.name === 'AbortError'
        ? 'Connection was interrupted. Please try again.'
        : (error?.message || 'Login failed');
      return { success: false, error: message };
    }
  };

  /**
   * Register new resident (requires super_admin approval before login)
   */
  const register = async (data) => {
    try {
      const { email, password, name, houseNumber, estateId, contact } = data || {};
      if (!email || !password || !houseNumber || !estateId) {
        return { success: false, error: 'Email, password, house number, and estate are required' };
      }
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role: 'resident',
            name: name?.trim() || email.split('@')[0],
            house_number: houseNumber,
            estate_id: estateId,
            contact: contact || null,
          },
        },
      });
      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (err) {
      return { success: false, error: err?.message || 'Registration failed' };
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  /**
   * Update user profile
   * @param {Object} userData - Updated user data
   */
  const updateUser = async (userData) => {
    try {
      const updateData = {};
      if (userData.name) updateData.name = userData.name;
      if (userData.phone) updateData.phone = userData.phone;
      if (userData.avatar) updateData.avatar = userData.avatar;

      const { data, error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message || 'Update failed' };
      }

      setUser((prev) => ({
        ...prev,
        name: data.name,
        phone: data.phone,
        avatar: data.avatar,
      }));

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Update failed' };
    }
  };

  /**
   * Change password
   * @param {Object} passwords - { newPassword }
   */
  const changePassword = async ({ newPassword }) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Password change failed' };
    }
  };

  /**
   * Re-check authentication status
   */
  const checkAuth = async () => {
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    if (currentSession) {
      const profile = await fetchProfile(currentSession.user.id);
      if (profile) {
        setUser({
          id: profile.id,
          name: profile.name,
          email: profile.email,
          phone: profile.phone,
          role: profile.role,
          avatar: profile.avatar,
          status: profile.status,
          createdAt: profile.created_at,
          lastLogin: profile.last_login,
        });
        setIsAuthenticated(true);
      }
    }
  };

  const value = {
    user,
    session,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
    changePassword,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
};

export default AuthContext;
